import { Gift, Users } from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import {
  getAdminV1StoresStoreIdCustomers,
  getAdminV1StoresStoreIdGiftCards,
} from '@nucleus-commerce/api-client/admin';

const newestCustomer: TourResolver = {
  key: 'newest-customer',
  thing: 'things.customer',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdCustomers({ storeId }, { limit: 1 });
    const customer = page.items[0];
    return customer ? { storeId, customerId: customer.id } : null;
  },
};

const newestGiftCard: TourResolver = {
  key: 'newest-gift-card',
  thing: 'things.giftCard',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdGiftCards({ storeId }, { limit: 1 });
    const card = page.items[0];
    return card ? { storeId, giftCardId: card.id } : null;
  },
};

export const customersListTour = defineTour({
  id: 'customers.list',
  version: 1,
  group: 'customers',
  route: '/_authed/_active/s/$storeId/customers/',
  scope: 'store',
  requires: 'customers.read',
  icon: Users,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'customers.list.table' },
        { target: 'customers.list.empty', copy: 'empty' },
      ],
    },
    {
      id: 'search',
      kind: 'action',
      target: 'customers.list.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'add', kind: 'explain', target: 'customers.list.add', requires: 'customers.write' },
    { id: 'privacy-link', kind: 'explain', target: 'customers.list.privacy-link' },
    {
      id: 'import-export',
      kind: 'explain',
      target: 'customers.list.import-export',
      requires: 'exports.run',
      optional: true,
    },
  ],
  related: ['customers.detail', 'privacy.list'],
});

export const customerDetailTour = defineTour({
  id: 'customers.detail',
  version: 1,
  group: 'customers',
  route: '/_authed/_active/s/$storeId/customers/$customerId',
  scope: 'record',
  resolve: newestCustomer,
  requires: 'customers.read',
  icon: Users,
  steps: [
    { id: 'overview', kind: 'explain', target: 'customers.detail.overview', placement: 'right' },
    {
      id: 'orders',
      kind: 'explain',
      target: 'customers.detail.orders',
      requires: 'orders.read',
      placement: 'right',
    },
    { id: 'addresses', kind: 'explain', target: 'customers.detail.addresses', placement: 'right' },
    {
      id: 'gift-cards',
      kind: 'explain',
      target: 'customers.detail.gift-cards',
      requires: 'gift_cards.read',
      placement: 'right',
    },
    { id: 'notes', kind: 'explain', target: 'customers.detail.notes', placement: 'left' },
    {
      id: 'more-menu',
      kind: 'explain',
      target: 'customers.detail.more-menu',
      requires: 'customers.write',
    },
  ],
  related: ['customers.list', 'draft-orders.list'],
});

export const giftCardsListTour = defineTour({
  id: 'gift-cards.list',
  version: 1,
  group: 'customers',
  route: '/_authed/_active/s/$storeId/gift-cards/',
  scope: 'store',
  requires: 'gift_cards.read',
  icon: Gift,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'gift-cards.list.table' },
        { target: 'gift-cards.list.empty', copy: 'empty' },
      ],
    },
    {
      id: 'search',
      kind: 'action',
      target: 'gift-cards.list.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'issue', kind: 'action', target: 'gift-cards.list.issue', requires: 'gift_cards.write' },
    {
      id: 'amount',
      kind: 'explain',
      target: 'gift-cards.issue.amount',
      requires: 'gift_cards.write',
      optional: true,
      waitMs: 1500,
    },
  ],
  related: ['gift-cards.detail'],
});

export const giftCardDetailTour = defineTour({
  id: 'gift-cards.detail',
  version: 1,
  group: 'customers',
  route: '/_authed/_active/s/$storeId/gift-cards/$giftCardId',
  scope: 'record',
  resolve: newestGiftCard,
  requires: 'gift_cards.read',
  icon: Gift,
  steps: [
    { id: 'balance', kind: 'explain', target: 'gift-cards.detail.balance', placement: 'right' },
    {
      id: 'transactions',
      kind: 'explain',
      target: 'gift-cards.detail.transactions',
      placement: 'top',
    },
    {
      id: 'adjust',
      kind: 'explain',
      target: 'gift-cards.detail.adjust',
      requires: 'gift_cards.write',
    },
    {
      id: 'toggle-status',
      kind: 'explain',
      target: 'gift-cards.detail.toggle-status',
      requires: 'gift_cards.write',
    },
  ],
  related: ['gift-cards.list'],
});
